// RENDERING THE SIGN-OFF VIEWS AT SEND (owner 2026-11-01): the real model, only the
// signed-off elements drawn (the viewer's isolate) and tinted in the star's colour, with the
// signed-off tiles over them; for each star the recipients sign as, the five angles of
// signoffCapture.js. Shown in the request window while it works - a canvas off screen is
// not reliably drawn by every browser - and handed back as [{ grant, kind, title, dataUrl }].
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Progress } from "antd";
import TileModelView from "./TileModelView";
import { ANGLES, captureAngles, clipBoxFor, settle, waitForModel } from "./signoffCapture";
import { RING } from "./signoffState";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// a tint is a colour per element; past this many the tiles alone carry the colour
const MAX_TINTED = 400000;
const STAR = { ownerGc: "blue", tenant: "gold", partner: "silver" };

export default function SignoffViewCapture({ projectId, tileView, tileKeys, uids, all = false, grants, onDone, onError }) {
  const controlRef = useRef(null);
  const [step, setStep] = useState({ grant: 0, shots: 0 });
  const grant = grants[step.grant] || grants[0];
  const hit = useMemo(() => new Set(tileKeys || []), [tileKeys]);
  const hitTiles = useMemo(() => ((tileView && tileView.tiles) || []).filter((t) => hit.has(t.key)), [tileView, hit]);
  // the tiles faint (outline and a light wash): the model's own elements are the picture -
  // a full tile fill buried them on staging (2026-11-01)
  const styleOf = useCallback(() => ({ color: RING[grant], strong: false }), [grant]);
  const visible = useCallback((t) => hit.has(t.key), [hit]);
  const isolate = all ? null : uids;
  // the picture is cut to the signed-off tiles (and a little round them): an element whose
  // centre is in them but that spans the building - a slab - covered every view on staging
  const clip = useMemo(() => (tileView && hitTiles.length ? clipBoxFor(tileView.grid, hitTiles) : null), [tileView, hitTiles]);
  // the grid a little inside it, so its bubbles are not cut in half by the clip
  const gridClip = useMemo(() => (clip ? clip.clone().expandByScalar(-tileView.grid.edge * 0.3) : null), [clip, tileView]);
  const tint = !all && uids && uids.length <= MAX_TINTED ? uids : null;
  const done = useRef({ onDone, onError });
  done.current = { onDone, onError };

  useEffect(() => {
    let dead = false;
    (async () => {
      const t0 = Date.now();
      while (!dead && !controlRef.current && Date.now() - t0 < 90000) await sleep(300); // eslint-disable-line no-await-in-loop
      if (dead) return;
      if (!controlRef.current) throw new Error("The model did not open in time");
      setStep({ grant: 0, shots: 0, loading: true });
      if (!(await waitForModel(controlRef.current.viewer))) throw new Error("The model did not load in time");
      if (dead) return;
      const out = [];
      for (let i = 0; i < grants.length; i += 1) {
        if (dead) return;
        setStep({ grant: i, shots: 0 });
        await sleep(500); // eslint-disable-line no-await-in-loop
        const { viewer } = controlRef.current || {};
        await settle(viewer, { maxMs: 30000 }); // eslint-disable-line no-await-in-loop
        // eslint-disable-next-line no-await-in-loop
        const shots = await captureAngles(viewer, tileView.grid, hitTiles, { clip, onShot: (n) => !dead && setStep({ grant: i, shots: n }) });
        shots.forEach((s) => out.push({ ...s, grant: grants[i] }));
      }
      if (!dead) done.current.onDone(out);
    })().catch((e) => { if (!dead) done.current.onError(e); });
    return () => { dead = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const total = grants.length * ANGLES.length;
  const made = step.grant * ANGLES.length + step.shots;
  return (
    <div className="signoff-box" data-testid="signoff-capture">
      <div className="signoff-box-title">
        {step.loading ? "Loading the model for the views…" : `Rendering the views for the email · ${STAR[grant]} star · ${Math.min(made, total)} of ${total}`}
      </div>
      <Progress percent={Math.round((100 * made) / total)} size="small" showInfo={false} />
      <TileModelView projectId={projectId} tiles={tileView} styleOf={styleOf} visible={visible} isolateUids={isolate}
                     highlightUids={tint} highlightColor={RING[grant]} height="420px" controlRef={controlRef} gridClip={gridClip} />
    </div>
  );
}
